import {
    BoxedRow,
    Circle,
    IconCalendarEuroRegular,
    IconChatRegular,
    IconCommentRegular,
    IconEmailSendRegular,
    IconFixedInternetRegular,
    IconInternetRegular,
    IconSupportAgentRegular,
    IconTechServiceRegular,
    IconUserSupportRegular,
    IconWarningRegular,
    SnapCard,
    Spinner,
    Stack,
    Text3,
    Text6,
    skinVars,
    useWindowSize,
} from '@telefonica/mistica';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { IProblemIssue } from '../../models/Problems.interfaces';
import { getListProblems } from '../../services/ProblemService';
import { getAllSupport } from '../../services/SupportService';
import navigateParams from '../../utils/navigation/navigateParams';
import { hideSpinner, showSnackbar, showStopper } from '../../utils/store/features/user';
import { ETypeUser } from '../../utils/store/model/store.model';
import './index.scss';
import { TrackingEvents } from '../../utils/dataTransformation/tracking';
import { isWebviewAndroid, isWebviewIOS } from '../../utils/navigation/controlDevices';
import { managementEnvironment } from '../../utils/dataTransformation/environments';
import useUserlight from '../../utils/hooks/useUserLight';
import useGlobalEscalation from '../../utils/globals/GlobalEscalation';
import { isIOS } from 'react-device-detect';

interface IActionSupport {
    icon: JSX.Element;
    title: string;
    description: string;
    type?: string;
    contact?: string;
}

const Support: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const size = useWindowSize();
    const isLight = useUserlight();
    const typeUser = new URLSearchParams(window.location.search).get('clientType');
    const clientType = typeUser;
    const { trackingScreenView } = new TrackingEvents();
    const { trackingUserInteraction } = new TrackingEvents();
    const [hasIncidents, setHasIncidents] = useState<IProblemIssue[]>();
    const [tabsSupport, setTabsSupport] = useState([]);
    const [errorProblems, setErrorProblems] = useState(false);
    const [errorSupport, setErroSupport] = useState(false);
    const [supportList, setSupportList] = useState([]);
    const [loading, setLoading] = useState<boolean>(true);
    const supportDisabled = managementEnvironment().REACT_APP_STOPPER_PROBLEMS_DISABLED;
    const changeBankAccountDisabled = managementEnvironment().REACT_APP_STOPPER_PROFILE_CUSTOMER_DATA_DISABLED;
    const { checkEscalation } = useGlobalEscalation();

    let actions = [
        {
            icon: <IconInternetRegular />,
            title: t('firstSteps'),
            description: t('firstStepsDescription'),
            type: 'URL',
        },
        {
            icon: <IconCommentRegular />,
            title: t('supportWhatsapp'),
            description: t('supportWhatsappDescription'),
            type: 'WHATSAPP',
        },
        {
            icon: <IconSupportAgentRegular />,
            title: t('supportPhone'),
            description: t('supportPhoneDescription'),
            type: 'PHONE',
        },
    ];

    const sendToNative = (
        url: string,
        type: string,
        title: string,
        id?: string,
        externalLink?: boolean,
        params?: IActionSupport,
    ) => {
        const queryParams = { title, id };
        if (url === '/support/incidents' || url === '/support/solve-incident') {
            if (supportDisabled) {
                dispatch(showStopper());
            } else {
                if (navigateParams(type, url, {}, queryParams, title)) {
                    if (type === 'modeFlow-backgroundBlue') {
                        navigate(url, { state: { hasIncidents } });
                    } else if (type === 'modeExternal') {
                        if (externalLink) {
                            window.open(url, '_blank');
                        } else {
                            window.location.href = url;
                        }
                    } else if (type === 'URL') {
                        const { contact } = params!;
                        window.location.href = contact as string;
                    }
                } else if (type === 'URL') {
                    const { contact } = params!;
                    window.location.href = contact as string;
                }
            }
        } else {
            if (navigateParams(type, url, {}, queryParams, title)) {
                if (type === 'modeFlow-backgroundBlue') {
                    navigate(url, { state: { hasIncidents } });
                } else if (type === 'modeExternal') {
                    if (externalLink) {
                        window.open(url, '_blank');
                    } else {
                        window.location.href = url;
                    }
                } else if (type === 'URL') {
                    const { contact } = params!;
                    window.location.href = contact as string;
                }
            }
        }
    };

    useEffect(() => {
        getInfoProblemsSupport()
            .then(() => {
                dispatch(hideSpinner());
                setLoading(false);
            })
            .catch(() => {
                dispatch(hideSpinner());
                setLoading(false);
                setErroSupport(true);
            });
        if (!(isWebviewAndroid && isWebviewIOS)) {
            trackingScreenView();
        }
    }, []);

    const getInfoProblemsSupport = async () => {
        let type = clientType;
        let promises = type === ETypeUser.PRECLIENT ? [getAllSupport()] : [getAllSupport(), getListProblems()];
        const [dataCards, problemsList]: any = await Promise.allSettled(promises);
        if (dataCards?.status === 'fulfilled') {
            const typeSupport = {
                PR: dataCards?.value?.data.helpInfoPreClient,
                NORMAL: dataCards?.value?.data.helpInfo,
            };
            setTabsSupport(typeSupport.NORMAL);
            if (type === ETypeUser.PRECLIENT) setSupportList(typeSupport.PR);
            if (type === ETypeUser.NORMAL) setSupportList(typeSupport.NORMAL);
        } else {
            setErroSupport(true);
        }
        if (problemsList) {
            if (problemsList?.status === 'fulfilled') {
                setHasIncidents(problemsList?.value?.data || []);
            } else {
                setErrorProblems(true);
            }
        }
        return [dataCards, problemsList];
    };

    const setSnapCard = (type: string, contact: string) => {
        if (type === 'PHONE') {
            return size.width >= 1024 ? (
                <SnapCard
                    icon={
                        <Circle size={40} backgroundColor={skinVars.colors.brandLow}>
                            <IconSupportAgentRegular size={24} color={skinVars.colors.brand} />
                        </Circle>
                    }
                    title={contact}
                ></SnapCard>
            ) : (
                <SnapCard
                    icon={
                        <Circle size={40} backgroundColor={skinVars.colors.brandLow}>
                            <IconSupportAgentRegular size={24} color={skinVars.colors.brand} />
                        </Circle>
                    }
                    title={contact}
                    onPress={() => {
                        trackingUserInteraction('contact_support_tapped');
                        sendToNative(`tel://${contact}`, 'modeExternal', '');
                    }}
                ></SnapCard>
            );
        } else if (type === 'WHATSAPP') {
            return (
                <SnapCard
                    icon={
                        <Circle size={40} backgroundColor={skinVars.colors.brandLow}>
                            <IconChatRegular size={24} color={skinVars.colors.brand} />
                        </Circle>
                    }
                    title={t('billsDebtWhatsappTitle')}
                    onPress={() => {
                        trackingUserInteraction('whatsapp_support_tapped');
                        sendToNative(`https://wa.me/${contact}`, 'modeExternal', '', '', true);
                    }}
                ></SnapCard>
            );
        } else if (type === 'EMAIL') {
            return (
                <SnapCard
                    icon={
                        <Circle size={40} backgroundColor={skinVars.colors.brandLow}>
                            <IconEmailSendRegular size={24} color={skinVars.colors.brand} />
                        </Circle>
                    }
                    title={t('supportEmailTitleSnapcard')}
                    onPress={() => {
                        trackingUserInteraction('email_support_tapped');
                        sendToNative(`mailto:${contact}`, 'modeExternal', '');
                    }}
                ></SnapCard>
            );
        } else if (type === 'URL') {
            if (size.width >= 1220) {
                return (
                    <SnapCard
                        icon={
                            <Circle size={40} backgroundColor={skinVars.colors.brandLow}>
                                <IconUserSupportRegular size={24} color={skinVars.colors.brand} />
                            </Circle>
                        }
                        title={t('supportHelpTitleBoxedRow')}
                        subtitle={t('supportHelpSubTitleBoxedRow')}
                        onPress={() => {
                            trackingUserInteraction('help_support_tapped');
                            sendToNative('https://o2online.es/ayuda/', 'modeExternal', '', '', true);
                        }}
                    ></SnapCard>
                );
            } else {
                return (
                    <BoxedRow
                        asset={
                            <Circle size={40} backgroundColor={skinVars.colors.brandLow}>
                                <IconUserSupportRegular size={24} color={skinVars.colors.brand} />
                            </Circle>
                        }
                        title={t('supportHelpTitleBoxedRow')}
                        description={t('supportHelpSubTitleBoxedRow')}
                        onPress={() => {
                            trackingUserInteraction('help_support_tapped');
                            sendToNative('https://o2online.es/ayuda/', 'modeExternal', '', '', true);
                        }}
                    ></BoxedRow>
                );
            }
        }
    };

    const getActionsSupport = () => {
        return supportList.map((action: IActionSupport, key: number) => {
            const { title, type, contact } = action;
            const icon = actions.filter((item: IActionSupport) => item.type === type)[0]?.icon;
            const description = actions.filter((item: IActionSupport) => item.type === type)[0]?.description;
            const redirections: { [key: string]: string } = {
                URL: 'https://o2online.es/ayuda/primeros-pasos/',
                WHATSAPP: `https://wa.me/${contact}`,
                PHONE: `tel://${contact}`,
            };
            const idTrackingObj: { [key: string]: string } = {
                URL: 'url_preclient_tapped',
                WHATSAPP: `whatsapp_preclient_tapped`,
                PHONE: `telf_preclient_tapped`,
            };
            const url = type ? redirections[type] : '';
            const idTracking = type ? idTrackingObj[type] : '';
            return (
                <div key={key} className='support-page--preclient-header-tabs'>
                    {size.width >= 1024 ? (
                        title === t('supportPhone') ? (
                            <BoxedRow
                                asset={icon}
                                title={title}
                                description={description ?? 'pendiente recibir respuesta'}
                            />
                        ) : (
                            <BoxedRow
                                asset={icon}
                                title={title}
                                description={description ?? 'pendiente recibir respuesta'}
                                onPress={() => {
                                    trackingUserInteraction(idTracking);
                                    sendToNative(url, 'modeExternal', title, type, false, action);
                                }}
                            />
                        )
                    ) : (
                        <BoxedRow
                            asset={icon}
                            title={title}
                            description={description ?? 'pendiente recibir respuesta'}
                            onPress={() => {
                                trackingUserInteraction(idTracking);
                                sendToNative(url, 'modeExternal', title, type, false, action);
                            }}
                        />
                    )}
                </div>
            );
        });
    };

    const isTypeUrl = () => {
        let aux;
        tabsSupport.map((item: any) => {
            if (item.type === 'URL') aux = true;
        });
        return aux;
    };

    return (
        <>
            {loading ? (
                <div className='c-spinner'>
                    <Spinner size={24} />
                    <span style={{ background: 'white' }} className='c-spinner-overlay'></span>
                </div>
            ) : (
                <div className={`${clientType === ETypeUser.PRECLIENT ? 'support-page--preclient' : 'support-page'}`}>
                    {!(isWebviewAndroid || isWebviewIOS) && (
                        <div
                            className={`${
                                clientType === ETypeUser.PRECLIENT
                                    ? 'support-page--preclient-title'
                                    : 'support-page-title'
                            }`}
                            style={{ background: skinVars.colors.navigationBarBackground }}
                        >
                            <Text6 color={skinVars.colors.textPrimaryInverse}>{t('breadcrumbsSupport')}</Text6>
                        </div>
                    )}
                    {clientType === ETypeUser.PRECLIENT ? (
                        <div>
                            <div
                                className='support-page--preclient-header'
                                style={{ background: skinVars.colors.backgroundBrand }}
                            >
                                {getActionsSupport()}
                            </div>
                        </div>
                    ) : (
                        <div className='support-page-background'>
                            <div
                                className='support-page__header'
                                style={{
                                    height:
                                        size.width >= 1220
                                            ? errorSupport
                                                ? '64px'
                                                : '149px'
                                            : tabsSupport.length === 4
                                            ? '186px'
                                            : tabsSupport.length === 2 && isTypeUrl()
                                            ? '186px'
                                            : tabsSupport.length === 1 || tabsSupport.length === 2
                                            ? '64px'
                                            : tabsSupport.length === 3 && isTypeUrl()
                                            ? '186px'
                                            : '64px',
                                    background: skinVars.colors.backgroundBrand,
                                }}
                            ></div>
                            <div className='support-page__content'>
                                <div
                                    className='support-page__content-tabs'
                                    style={{
                                        width: size.width >= 1220 && tabsSupport.length === 1 ? '344px' : 'auto',
                                        display: errorSupport
                                            ? 'inline-flex'
                                            : tabsSupport.length === 2 && isTypeUrl() && size.width >= 1220
                                            ? 'flex'
                                            : tabsSupport.length === 4 ||
                                              tabsSupport.length === 3 ||
                                              tabsSupport.length === 1
                                            ? 'flex'
                                            : 'block',
                                    }}
                                >
                                    {errorSupport ? (
                                        <div className='support-page__content-tabs-error'>
                                            <BoxedRow
                                                asset={<IconWarningRegular color={skinVars.colors.error} size={24} />}
                                                title={t('supportErrorProblemsTitle')}
                                                description={t('supportErrorProblemsDescription')}
                                            ></BoxedRow>
                                        </div>
                                    ) : (
                                        tabsSupport?.map((item: any, index: number) => {
                                            return (
                                                <div
                                                    className='support-page__content-tabs-card'
                                                    key={index}
                                                    style={{
                                                        flex:
                                                            size.width >= 1220
                                                                ? ''
                                                                : tabsSupport.length >= 4
                                                                ? '1 0 30%'
                                                                : tabsSupport.length >= 3 && isTypeUrl()
                                                                ? size.width >= 768
                                                                    ? '1 0 40%'
                                                                    : '1 0 30%'
                                                                : '1 0 30%',
                                                        marginTop: isTypeUrl() ? '16px' : '0px',
                                                    }}
                                                >
                                                    {setSnapCard(item.type, item.contact)}
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                                <div
                                    className='support-page__content-incidents'
                                    style={{
                                        background: skinVars.colors.backgroundContainer,
                                    }}
                                >
                                    <div className='support-page__content-incidents-titleManagement'>
                                        <Text3 medium color={skinVars.colors.textPrimary}>
                                            {t('supportContentTitleManagement')}
                                        </Text3>
                                    </div>
                                    <BoxedRow
                                        asset={<IconCalendarEuroRegular color={skinVars.colors.brand} size={24} />}
                                        title={t('supportContentRowChangeBankAccount')}
                                        onPress={() => {
                                            if (changeBankAccountDisabled) {
                                                dispatch(showStopper());
                                            } else {
                                                if (isLight) {
                                                    dispatch(showSnackbar());
                                                } else {
                                                    if (isIOS) {
                                                        const newWindow = window.open('', 'newWindow');
                                                        sessionStorage.setItem('newWindow', newWindow?.name || '');
                                                    }
                                                    checkEscalation('/select-bank-account', 'supportChangeBankAccount');
                                                }
                                            }
                                            trackingUserInteraction('support_change_bank_account_tapped');
                                        }}
                                    ></BoxedRow>
                                    <div className='support-page__content-incidents-title'>
                                        <Text3 medium color={skinVars.colors.textPrimary}>
                                            {t('supportContentTitle')}
                                        </Text3>
                                    </div>
                                    <Stack space={size.width >= 1024 ? 24 : 16}>
                                        <BoxedRow
                                            asset={
                                                <Circle backgroundColor={skinVars.colors.brandLow} size={40}>
                                                    <IconTechServiceRegular color={skinVars.colors.brand} size={24} />
                                                </Circle>
                                            }
                                            title={t('supportContentRowSolveIncidentTitle')}
                                            description={t('supportContentRowSolveIncidentDescription')}
                                            onPress={() => {
                                                trackingUserInteraction('solve_incident_support_tapped');
                                                sendToNative(
                                                    '/support/solve-incident',
                                                    'modeFlow-backgroundBlue',
                                                    t('supportContentRowSolveIncidentTitle'),
                                                );
                                            }}
                                        ></BoxedRow>
                                        {errorProblems ? (
                                            <BoxedRow
                                                asset={<IconWarningRegular color={skinVars.colors.error} size={24} />}
                                                title={t('supportErrorProblemsTitle')}
                                                description={t('supportErrorProblemsDescription')}
                                            ></BoxedRow>
                                        ) : (
                                            <BoxedRow
                                                asset={
                                                    <IconFixedInternetRegular color={skinVars.colors.brand} size={24} />
                                                }
                                                title={t('supportContentRowQueryIncidenceTitle')}
                                                onPress={() => {
                                                    trackingUserInteraction('consult_incident_support_tapped');
                                                    sendToNative(
                                                        '/support/incidents',
                                                        'modeFlow-backgroundBlue',
                                                        t('returnSupportIncidents'),
                                                    );
                                                }}
                                            ></BoxedRow>
                                        )}
                                    </Stack>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default Support;
